import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { BookingScope, useGetBookingsQuery } from '@graphql';
import type { Booking } from './types';
import { useBookingCredentials } from './useBookingCredentials';

// How often the label is worked out again. The countdown moves on its own
// without asking the booking system anything: what is on screen is a function
// of the booking and the clock, and only the clock is changing.
const TICK_MS = 60_000;

// The one being shown, the one after it, and one spare — so a booking that
// turns out to have finished does not leave the card empty.
const LOOKAHEAD = 3;

/** Phases a booking can be in and still be somebody's next appointment. */
const AHEAD = ['upcoming', 'in_progress'];

/** Whose diary to read: one customer's appointments, or one provider's. */
export type AppointmentScope = { customerEmail?: string | null; providerId?: string | null };

/**
 * The next appointment in that diary, and the one after it.
 *
 * Read from the booking system rather than Taper's own table, so an
 * appointment booked anywhere counts — the showroom's own diary is not the
 * only way one gets made. That also means it needs usable credentials, and
 * answers nothing without them rather than quietly showing somebody as free.
 *
 * A customer is matched on email, which is all the booking system holds to
 * join on; a provider by its own id.
 */
export const useNextAppointment = ({ customerEmail, providerId }: AppointmentScope) => {
  const { canReadAllBookings } = useBookingCredentials();
  const email = customerEmail?.trim() || null;
  const provider = providerId || null;

  const { data, loading } = useGetBookingsQuery({
    variables: {
      scope: BookingScope.All,
      customerEmail: email,
      providerId: provider ? [provider] : undefined,
      // Today rather than now: an appointment that started an hour ago may
      // still be running, and the phase filter is what decides.
      from: dayjs().format('YYYY-MM-DD'),
      phase: AHEAD,
      page: 1,
      pageSize: LOOKAHEAD,
    },
    // One scope or the other; neither means there is nothing to ask about.
    skip: (!email && !provider) || !canReadAllBookings,
    // The card is a glance at live state, so a cached answer from the last
    // visit is shown at once and corrected behind it.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  // Re-renders the label each minute. Held as state rather than read during
  // render so the countdown actually moves; the timer is the only thing that
  // changes, and it touches no query.
  const [now, setNow] = useState(() => dayjs());
  useEffect(() => {
    const tick = setInterval(() => setNow(dayjs()), TICK_MS);
    return () => clearInterval(tick);
  }, []);

  const items: Booking[] = data?.bookings2?.items ?? [];

  return {
    booking: items[0] ?? null,
    following: items[1] ?? null,
    // Only while there is nothing to show: a refetch behind an appointment
    // already on screen must not blank it.
    loading: loading && items.length === 0,
    now,
    // Nothing to render at all — no booking configured, or nobody to read for.
    isEnabled: (!!email || !!provider) && canReadAllBookings,
  };
};
